import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { useParams } from 'react-router-dom';
import './PDFViewer.css'; // Ensure to import a CSS file for additional styles
import { useAuth } from '../../../../firebase/AuthProvider';
import { getUserFileById } from '../../../../services/fileServices/UserDriverService';
import { Button, Form } from 'react-bootstrap';

// Set worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const UserFilePDFViewer: React.FC<{ fileUrl: string; scale: number }> = ({ fileUrl, scale }) => {
  const { currentUser } = useAuth();
  const { fileId } = useParams(); // Using useParams to get fileId from the URL
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>("1");
  const [file, setFile] = useState<string | null>();

  useEffect(() => {
    const fetchPDF = async () => {
      if (currentUser && fileId) {
        try {
          const authToken: string = await currentUser.getIdToken();
          const file = await getUserFileById(fileId, authToken);
          setFile(file); // Creates a local URL for the fetched blob
        } catch (error) {
          console.error('Error fetching PDF:', error);
        }
      }
    };

    fetchPDF();
  }, [fileId, currentUser]); // Effect runs when fileId changes

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInput, 10);
    if (!isNaN(pageNumber) && numPages && pageNumber >= 1 && pageNumber <= numPages) {
      setCurrentPage(pageNumber);
    } else {
      setPageInput(currentPage.toString());
    }
  };

  const handlePageInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePageInputBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 justify-start items-center">
        {numPages && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline-secondary"
              className="btn_sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <div className="flex items-center">
              <Form.Control
                type="number"
                value={pageInput}
                onChange={handlePageInputChange}
                onBlur={handlePageInputBlur}
                onKeyPress={handlePageInputKeyPress}
                className="w-16 text-center form-control-sm"
                min="1"
                max={numPages}
              />
              <span className="text-sm min-w-10 text-gray-500 ml-1 flex flex-col">{"/ " + numPages}</span>
            </div>
            <Button
              variant="outline-secondary"
              className="btn_sm"
              onClick={() => numPages && setCurrentPage((p) => Math.min(numPages, p + 1))}
              disabled={!numPages || currentPage >= numPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
      <div className='pdfViewer' style={{ maxHeight: 'calc(100vh - 250px)' }}>
        {file && (
          <Document className='bg-secondary dark:bg-secondary-dark' file={file} onLoadSuccess={onDocumentLoadSuccess}>
            <Page
              className='bg-secondary'
              pageNumber={currentPage}
              renderTextLayer={false}
              scale={scale} // Set the scale to control zoom level
            />
          </Document>
        )}
      </div>
    </div>
  );
};

export default UserFilePDFViewer;
